import { magento } from "@jti/magento";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

const X_SELLING_POPUP_DISPLAY_STORAGE_KEY = "xSellingPopupDisplayCountByCart";

type XSellingPopupDisplayCountByCart = Record<string, number>;

export type XSellingPopupSkusResult = {
  shouldDisplay: boolean;
  skus: string[];
};

const getStoredDisplayCounts = (): XSellingPopupDisplayCountByCart => {
  try {
    const storedValue = window.localStorage.getItem(X_SELLING_POPUP_DISPLAY_STORAGE_KEY);

    if (!storedValue) {
      return {};
    }

    return JSON.parse(storedValue) as XSellingPopupDisplayCountByCart;
  } catch {
    return {};
  }
};

const persistDisplayCounts = (displayCounts: XSellingPopupDisplayCountByCart) => {
  window.localStorage.setItem(X_SELLING_POPUP_DISPLAY_STORAGE_KEY, JSON.stringify(displayCounts));
};

const incrementDisplayCountForCart = (cartId: string) => {
  const displayCounts = getStoredDisplayCounts();

  displayCounts[cartId] = (displayCounts[cartId] ?? 0) + 1;

  persistDisplayCounts(displayCounts);
};

const hasDisplayRecordForCart = (cartId: string): boolean => cartId in getStoredDisplayCounts();

const getDisplayCountForCart = (cartId: string): number => getStoredDisplayCounts()[cartId] ?? 0;

const isValidDisplayLimit = (displayLimit: number | null | undefined): displayLimit is number =>
  typeof displayLimit === "number" && Number.isInteger(displayLimit) && displayLimit > 0;

export const getXSellingPopupDataForCart = async (cartId?: string): Promise<XSellingPopupSkusResult> => {
  if (!cartId) return { shouldDisplay: false, skus: [] };

  try {
    const response = await magento.getXSellingPopup(cartId);
    const crossSellPopup = response.cart.cross_sell_popup;
    const maxDisplaysPerSession = crossSellPopup?.max_displays_per_session;
    const offerSkus = crossSellPopup?.offer?.map((offer) => offer.sku) ?? [];
    const ruleId = crossSellPopup?.id;

    if (!crossSellPopup || !isValidDisplayLimit(maxDisplaysPerSession)) {
      return { shouldDisplay: false, skus: [] };
    }

    if (getDisplayCountForCart(cartId) >= maxDisplaysPerSession) {
      return { shouldDisplay: false, skus: [] };
    }

    if (ruleId && !hasDisplayRecordForCart(cartId)) {
      dispatchTrackingEvent(TrackingEvents.TrackingCrossSellPopupImpression, {
        rule_id: ruleId,
        offer_sku: offerSkus,
      });
    }

    incrementDisplayCountForCart(cartId);

    return { shouldDisplay: true, skus: offerSkus };
  } catch {
    return { shouldDisplay: false, skus: [] };
  }
};
